// -------------------------------------------------------------
// Porosity — programmatic Web Audio sound engine (UI module).
//
// All sounds are synthesised at runtime — no external audio
// files. The AudioContext is created *only after* the first user
// gesture (per browser autoplay policies), and every sound
// respects the session-scoped `muted` flag.
//
// Public API:
//   S.<eventName>()   — trigger a sound (no-op if muted / no ctx)
//   useMuted()         — [muted, toggle] React hook
//   toggleMuted()     — flip the mute flag
//   setMuted(bool)    — set explicitly
//   isMuted()         — current value
// -------------------------------------------------------------
import { useEffect, useState } from "react";

let ctx = null;
let master = null;
let muted = false;
let gestureUnlocked = false;

// -------------------------------------------------------------
// Mute state — session-scoped subscriber pattern.
// -------------------------------------------------------------
const listeners = new Set();
function notify() {
  for (const l of listeners) {
    try { l(muted); } catch { /* ignore listener errors */ }
  }
}
export function subscribeMute(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function isMuted() { return muted; }
export function setMuted(v) {
  muted = !!v;
  if (master) master.gain.value = muted ? 0 : 1;
  if (muted) stopAll();
  notify();
}

// -------------------------------------------------------------
// Live sources — muting stops everything still playing (long weather
// sounds, scheduled patter), not just future sounds.
// -------------------------------------------------------------
const live = new Set();
function track(src) {
  live.add(src);
  src.onended = () => live.delete(src);
  return src;
}
function stopAll() {
  for (const src of live) {
    try { src.stop(); } catch { /* already stopped */ }
  }
  live.clear();
}
export function toggleMuted() { setMuted(!muted); }

export function useMuted() {
  const [m, setM] = useState(muted);
  useEffect(() => subscribeMute(setM), []);
  return [m, toggleMuted];
}

// -------------------------------------------------------------
// AudioContext bootstrapping — lazy, gesture-gated.
// -------------------------------------------------------------
function ensureCtx() {
  if (!gestureUnlocked || muted) return null;
  if (ctx) {
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    return ctx;
  }
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  master = ctx.createGain();
  master.gain.value = muted ? 0 : 1;
  master.connect(ctx.destination);
  return ctx;
}

function unlockOnGesture() {
  if (gestureUnlocked) return;
  gestureUnlocked = true;
  ensureCtx();
}

if (typeof window !== "undefined") {
  const h = () => unlockOnGesture();
  window.addEventListener("pointerdown", h, { once: true, capture: true });
  window.addEventListener("keydown", h, { once: true, capture: true });
  window.addEventListener("touchstart", h, { once: true, capture: true });
}

// -------------------------------------------------------------
// Low-level primitives.
// -------------------------------------------------------------
function noiseBuffer(c, seconds) {
  const len = Math.max(1, Math.floor(c.sampleRate * seconds));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

function tone({
  freq,
  dur,
  type = "sine",
  peak = 0.2,
  attack = 0.005,
  delay = 0,
  sweepTo = null,
  sweepAt = 0,
}) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + delay;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (sweepTo !== null) {
    osc.frequency.linearRampToValueAtTime(sweepTo, t0 + (sweepAt || dur));
  }
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(master);
  track(osc);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

function noise({
  dur,
  peak = 0.15,
  filterType = "bandpass",
  freq = 1000,
  Q = 1,
  attack = 0.005,
  delay = 0,
  sweepTo = null,
}) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + delay;
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, dur);
  const filt = c.createBiquadFilter();
  filt.type = filterType;
  filt.frequency.setValueAtTime(freq, t0);
  if (sweepTo !== null) filt.frequency.linearRampToValueAtTime(sweepTo, t0 + dur);
  filt.Q.value = Q;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filt).connect(g).connect(master);
  track(src);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

// Filtered noise whose loudness pulses at `rate` Hz (cicadas, sizzle, wind
// gusts). `swell` = seconds to reach the peak; fades out over the last 35 %.
function buzz({
  dur,
  peak = 0.1,
  filterType = "bandpass",
  freq = 4000,
  Q = 6,
  rate = 40,
  depth = 0.9,
  swell = 0.15,
  delay = 0,
  sweepTo = null,
}) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + delay;
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, dur);
  const filt = c.createBiquadFilter();
  filt.type = filterType;
  filt.frequency.setValueAtTime(freq, t0);
  if (sweepTo !== null) filt.frequency.linearRampToValueAtTime(sweepTo, t0 + dur);
  filt.Q.value = Q;
  // Tremolo: gain = (1 - depth / 2) + depth / 2 * sin(rate t)
  const trem = c.createGain();
  trem.gain.value = 1 - depth / 2;
  const lfo = c.createOscillator();
  lfo.frequency.value = rate;
  const lfoAmt = c.createGain();
  lfoAmt.gain.value = depth / 2;
  lfo.connect(lfoAmt).connect(trem.gain);
  const env = c.createGain();
  env.gain.setValueAtTime(0.0001, t0);
  env.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), t0 + Math.max(0.01, swell));
  env.gain.setValueAtTime(Math.max(0.0001, peak), t0 + dur * 0.65);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filt).connect(trem).connect(env).connect(master);
  track(src);
  track(lfo);
  src.start(t0);
  lfo.start(t0);
  src.stop(t0 + dur + 0.02);
  lfo.stop(t0 + dur + 0.02);
}

// A wave: low-passed noise that swells up, breaks (filter opens) and washes out.
function wave({ delay = 0, dur = 1.4, peak = 0.3, from = 250, to = 1800 }) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + delay;
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, dur);
  const filt = c.createBiquadFilter();
  filt.type = "lowpass";
  filt.frequency.setValueAtTime(from, t0);
  filt.frequency.exponentialRampToValueAtTime(to, t0 + dur * 0.45);
  filt.frequency.exponentialRampToValueAtTime(from * 1.5, t0 + dur);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), t0 + dur * 0.42);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filt).connect(g).connect(master);
  track(src);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

// -------------------------------------------------------------
// CARD SOUNDS
// -------------------------------------------------------------
function cardHover() {
  noise({ dur: 0.09, peak: 0.05, filterType: "bandpass", freq: 2800, Q: 0.6 });
}
function cardSelect() {
  tone({ freq: 160, dur: 0.13, type: "sine", peak: 0.28 });
  tone({ freq: 90, dur: 0.15, type: "triangle", peak: 0.18 });
  noise({ dur: 0.08, peak: 0.08, filterType: "lowpass", freq: 800, Q: 0.7 });
}
function cardDraw() {
  tone({ freq: 320, dur: 0.06, type: "square", peak: 0.1 });
  noise({ dur: 0.05, peak: 0.06, filterType: "highpass", freq: 1500, Q: 0.8 });
}

// -------------------------------------------------------------
// TILE / TOKEN PLACEMENT
// -------------------------------------------------------------
function placeBuildingFloor() {
  // Heaviest sound family — deep industrial concrete-drop thud.
  tone({ freq: 55, dur: 0.32, type: "sine", peak: 0.42 });
  tone({ freq: 40, dur: 0.4, type: "sine", peak: 0.28 });
  noise({ dur: 0.14, peak: 0.18, filterType: "lowpass", freq: 300, Q: 1 });
  noise({ dur: 0.05, peak: 0.09, filterType: "bandpass", freq: 1500, Q: 2 });
}
function placeGray() {
  tone({ freq: 110, dur: 0.16, type: "sine", peak: 0.25 });
  tone({ freq: 70, dur: 0.18, type: "triangle", peak: 0.15 });
  noise({ dur: 0.08, peak: 0.1, filterType: "lowpass", freq: 500, Q: 0.8 });
}
function placeToken() {
  // Higher & shorter than the gray tile thud.
  tone({ freq: 220, dur: 0.07, type: "triangle", peak: 0.18 });
  noise({ dur: 0.04, peak: 0.05, filterType: "highpass", freq: 1200 });
}
function birdCall(delay = 0.18) {
  // Two-note rising whistle — small songbird.
  tone({ freq: 1600, sweepTo: 2200, sweepAt: 0.09, dur: 0.09, type: "sine", peak: 0.14, delay });
  tone({ freq: 2100, sweepTo: 2600, sweepAt: 0.08, dur: 0.08, type: "sine", peak: 0.12, delay: delay + 0.13 });
}
function waterBurble(delay = 0.16) {
  // Soft trickle sweeping up.
  noise({ dur: 0.35, peak: 0.12, filterType: "bandpass", freq: 500, Q: 4, sweepTo: 900, delay });
  tone({ freq: 480, sweepTo: 720, sweepAt: 0.2, dur: 0.22, type: "sine", peak: 0.09, delay: delay + 0.02 });
}
function placeGreenTile()  { placeGray(); birdCall(0.16); }
function placeBlueTile()   { placeGray(); waterBurble(0.14); }
function placeGreenToken() { placeGray(); birdCall(0.14); }
function placeBlueToken()  { placeGray(); waterBurble(0.14); }

// -------------------------------------------------------------
// WATER & DROPS
// -------------------------------------------------------------
function coinPing() {
  tone({ freq: 1400, dur: 0.14, type: "sine", peak: 0.14 });
  tone({ freq: 2100, dur: 0.12, type: "sine", peak: 0.08, delay: 0.02 });
}
function coinChime() {
  tone({ freq: 880, dur: 0.16, type: "sine", peak: 0.16 });
  tone({ freq: 1320, dur: 0.16, type: "sine", peak: 0.14, delay: 0.06 });
  tone({ freq: 1760, dur: 0.22, type: "sine", peak: 0.12, delay: 0.12 });
}

// -------------------------------------------------------------
// WEATHER / GLOBAL CARDS
// -------------------------------------------------------------
// A card turns over: a soft paper swish and a small tap when it lands.
function cardFlip() {
  noise({ dur: 0.16, peak: 0.07, filterType: "bandpass", freq: 2200, Q: 0.8 });
  tone({ freq: 220, sweepTo: 150, sweepAt: 0.1, dur: 0.14, type: "triangle", peak: 0.12, delay: 0.3 });
  noise({ dur: 0.04, peak: 0.06, filterType: "lowpass", freq: 900, Q: 0.8, delay: 0.3 });
}
// AREA SHUFFLE: a soft tick per decoy jump (they slow down with the shuffle) ...
function areaTick() {
  tone({ freq: 880, dur: 0.03, type: "triangle", peak: 0.07 });
  noise({ dur: 0.02, peak: 0.03, filterType: "bandpass", freq: 2400, Q: 1.2 });
}
// ... and a low thud when the real area freezes (before the face's own sound).
function areaThud() {
  tone({ freq: 90, sweepTo: 48, sweepAt: 0.16, dur: 0.3, type: "sine", peak: 0.42 });
  tone({ freq: 60, dur: 0.32, type: "triangle", peak: 0.16 });
  noise({ dur: 0.1, peak: 0.12, filterType: "lowpass", freq: 420, Q: 0.9 });
}

// -------------------------------------------------------------
// WEATHER RESULTS — scaled by seriousness (length and loudness):
// Clear Skies < Light Rain < Heat Wave < Heavy Rain < Heat Storm < Storm Surge
// -------------------------------------------------------------
function weatherClearSkies() {
  // Light, bright chime + a little birdsong (~0.9 s).
  tone({ freq: 1568, dur: 0.35, type: "sine", peak: 0.09 });
  tone({ freq: 2093, dur: 0.35, type: "sine", peak: 0.08, delay: 0.09 });
  tone({ freq: 2637, dur: 0.45, type: "sine", peak: 0.07, delay: 0.18 });
  birdCall(0.38);
  tone({ freq: 2300, sweepTo: 3000, sweepAt: 0.06, dur: 0.06, type: "sine", peak: 0.08, delay: 0.72 });
}
function weatherLightRain() {
  // Soft, short patter (~1 s).
  for (let i = 0; i < 18; i++) {
    noise({
      dur: 0.04,
      peak: 0.05,
      filterType: "highpass",
      freq: 4000,
      delay: i * 0.05 + Math.random() * 0.02,
    });
  }
  noise({ dur: 0.9, peak: 0.025, filterType: "highpass", freq: 5000, Q: 0.5 });
}
function weatherHeavyRain() {
  // Heavier, longer downpour (~2.4 s) + a low thunder rumble.
  for (let i = 0; i < 48; i++) {
    noise({
      dur: 0.06,
      peak: 0.08,
      filterType: "bandpass",
      freq: 1200 + Math.random() * 800,
      Q: 0.9,
      delay: i * 0.045 + Math.random() * 0.02,
    });
  }
  noise({ dur: 2.4, peak: 0.08, filterType: "lowpass", freq: 900, attack: 0.25 });
  // Thunder: a low rumble rolling in after the rain starts.
  noise({ dur: 1.6, peak: 0.24, filterType: "lowpass", freq: 140, Q: 0.7, attack: 0.18, delay: 0.55 });
  tone({ freq: 48, sweepTo: 36, sweepAt: 1.4, dur: 1.5, type: "sine", peak: 0.18, attack: 0.15, delay: 0.6 });
}
function weatherHeatWave() {
  // Low warm hum + cicada buzz (~1.7 s).
  tone({ freq: 110, dur: 1.7, type: "sine", peak: 0.1, attack: 0.3 });
  tone({ freq: 165, dur: 1.6, type: "sine", peak: 0.05, attack: 0.35, delay: 0.05 });
  buzz({ dur: 1.5, peak: 0.07, freq: 4600, Q: 7, rate: 38, depth: 0.95, swell: 0.3, delay: 0.15 });
}
function weatherExtremeHeat() {
  // Louder, harsher, longer sizzle / buzz (~2.8 s).
  tone({ freq: 82, dur: 2.8, type: "sawtooth", peak: 0.07, attack: 0.4 });
  tone({ freq: 123, dur: 2.6, type: "sawtooth", peak: 0.04, attack: 0.45, delay: 0.1 });
  buzz({ dur: 2.7, peak: 0.13, freq: 5200, Q: 5, rate: 55, depth: 1, swell: 0.35 });
  buzz({ dur: 2.4, peak: 0.08, filterType: "highpass", freq: 6500, Q: 0.7, rate: 13, depth: 0.7, swell: 0.5, delay: 0.3 });
}
function weatherStormSurge() {
  // The strongest: wind + crashing waves, the longest of all (~3.8 s).
  buzz({ dur: 3.8, peak: 0.16, freq: 500, Q: 1.4, rate: 0.7, depth: 0.8, swell: 0.6, sweepTo: 1300 });
  buzz({ dur: 3.2, peak: 0.07, filterType: "highpass", freq: 2400, Q: 0.6, rate: 1.3, depth: 0.7, swell: 0.8, delay: 0.4 });
  tone({ freq: 45, sweepTo: 70, sweepAt: 3.2, dur: 3.6, type: "sine", peak: 0.26, attack: 0.5 });
  wave({ delay: 0.3, dur: 1.4, peak: 0.28 });
  wave({ delay: 1.4, dur: 1.5, peak: 0.36, to: 2200 });
  wave({ delay: 2.4, dur: 1.4, peak: 0.3 });
}
// The result sound for a weather face key.
function weatherResult(face) {
  switch (face) {
    case "clear_skies":
    case "sunny_day": weatherClearSkies(); break;
    case "light_rain": weatherLightRain(); break;
    case "heavy_rain": weatherHeavyRain(); break;
    case "heat_wave": weatherHeatWave(); break;
    case "heat_storm":
    case "extreme_heat_wave": weatherExtremeHeat(); break;
    case "storm_surge": weatherStormSurge(); break;
    default: break;
  }
}

// -------------------------------------------------------------
// SLR
// -------------------------------------------------------------
function slrRise() {
  // The most dramatic sound in the game — 3 seconds of environmental
  // dread building to a crashing wave.
  tone({ freq: 30, sweepTo: 60, sweepAt: 2.6, dur: 3.0, type: "sine", peak: 0.5 });
  tone({ freq: 45, sweepTo: 90, sweepAt: 2.6, dur: 3.0, type: "sine", peak: 0.3, delay: 0.1 });
  noise({ dur: 3.0, peak: 0.35, filterType: "lowpass", freq: 300, sweepTo: 2200 });
  tone({ freq: 110, dur: 3.0, type: "sawtooth", peak: 0.08 });
  tone({ freq: 165, dur: 3.0, type: "sawtooth", peak: 0.05, delay: 0.2 });
  noise({ dur: 1.4, peak: 0.35, filterType: "bandpass", freq: 1800, Q: 0.5, delay: 1.6 });
}

// -------------------------------------------------------------
// TURN / GAME EVENTS
// -------------------------------------------------------------
function turnClick() {
  tone({ freq: 520, dur: 0.05, type: "triangle", peak: 0.12 });
  noise({ dur: 0.03, peak: 0.04, filterType: "highpass", freq: 2000 });
}
function gameEnd() {
  // Warm major-triad resolution.
  tone({ freq: 261.63, dur: 1.6, type: "sine", peak: 0.16 });
  tone({ freq: 329.63, dur: 1.6, type: "sine", peak: 0.14, delay: 0.08 });
  tone({ freq: 392.0,  dur: 1.8, type: "sine", peak: 0.14, delay: 0.16 });
  tone({ freq: 523.25, dur: 1.8, type: "sine", peak: 0.1,  delay: 0.24 });
}

// -------------------------------------------------------------
// Category sets — mirror the game's infrastructure categories.
// -------------------------------------------------------------
export const GREEN_SOUND_KEYS = new Set([
  "urban_park",
  "urban_forest",
  "green_roof",
  "green_street",
]);
export const BLUE_SOUND_KEYS = new Set([
  "mangrove_forest",
  "inland_wetland",
]);

// -------------------------------------------------------------
// Public sound bank.
// -------------------------------------------------------------
export const S = {
  cardHover,
  cardSelect,
  cardDraw,
  placeBuildingFloor,
  placeGray,
  placeGreenTile,
  placeBlueTile,
  placeGreenToken,
  placeBlueToken,
  placeToken,
  coinPing,
  coinChime,
  cardFlip,
  areaTick,
  areaThud,
  weatherClearSkies,
  weatherLightRain,
  weatherHeavyRain,
  weatherHeatWave,
  weatherExtremeHeat,
  weatherStormSurge,
  weatherResult,
  slrRise,
  turnClick,
  gameEnd,
};

export default S;
